import { Except } from 'type-fest';
import type { ProductSearchByImageFeatureOptions } from '../../../api/models/FeatureOptions';
import type { ProductSearchByImageFeatureItem } from '../../../models/features/main/ProductSearchByImageFeatureItem';
import type { AppDispatch, RootState } from '../../../store/store';
import { apiClient } from '../../ApiClient';
import { getNewFeatureUuid } from '../../FeatureService';
import type { FeatureHandler } from '../FeatureHandler';
import type {
    ChatMessage,
    Feature
} from '@public-api-contracts/models/chat/ChatMessage';
import { CategoryBoundingBox } from '../../../models/Product';

export const getBiggestBoundingBox = (
    boundingBoxes: CategoryBoundingBox[]
): CategoryBoundingBox | undefined => {
    return boundingBoxes.sort(
        (a, b) => b.width * b.height - a.width * a.height
    )[0];
};

async function upload(
    dispatch: AppDispatch,
    newMainFeatureItem: ProductSearchByImageFeatureItem,
    options: { imageDataUrl: string; shouldOpenSidebar: boolean }
) {
    const feature: Feature = {
        featureType: 'product-search-by-image',
        featureUuid: newMainFeatureItem.featureUuid,
        request: {
            imageDataUrl: options.imageDataUrl
        }
    };
    const newPartialChatMessage = {
        role: 'assistant',
        content: {
            contentType: 'feature',
            startedBy: 'user-from-ui',
            status: 'loading',
            feature
        }
    } as const satisfies Except<ChatMessage, 'timestamp' | 'uuid'>;
    const newChatMessage = dispatch.featureState.appendNewChatMessage(
        newPartialChatMessage
    );
    try {
        const { id, url, boundingBoxes, width, height } =
            await apiClient.assistant.identifyImageProducts({
                body: { base64: options.imageDataUrl.split(',')[1] }
            });

        dispatch.featureState.setSidebarExpanded(true);
        dispatch.featureState.updateMainFeatureItem({
            ...newMainFeatureItem,
            imageId: id,
            imageUrl: url,
            boundingBoxes,
            selectedBoundingBox: getBiggestBoundingBox(boundingBoxes),
            imageWidth: width,
            imageHeight: height,
            loadingStatus: 'success'
        });
        await dispatch.featureState.updateContext();
        if (newChatMessage) {
            dispatch.featureState.patchExistingChatMessage({
                uuid: newChatMessage.uuid,
                content: {
                    contentType: 'feature',
                    startedBy: 'user-from-ui',
                    status: 'success',
                    feature: {
                        ...feature,
                        featureUuid: newMainFeatureItem.featureUuid,
                        result: {
                            imageId: id,
                            imageUrl: url,
                            boundingBoxes,
                            imageWidth: width,
                            imageHeight: height
                        }
                    }
                }
            });
            if (options.shouldOpenSidebar) {
                dispatch.featureState.setSidebarExpanded(true);
            }
            dispatch.featureState.appendProductSearchByImageMessage();
        }
    } catch (e) {
        dispatch.featureState.setMainFeatureItem({
            ...newMainFeatureItem,
            loadingStatus: 'error',
            message: ''
        });
    }
}

export const productSearchByImageMainFeatureHandler = {
    async startFeatureItem(
        dispatch: AppDispatch,
        state: RootState,
        options: ProductSearchByImageFeatureOptions
    ): Promise<void> {
        const shouldOpenSidebar = !state.featureState.mainFeatureItem;
        const newMainFeatureItem: ProductSearchByImageFeatureItem = {
            featureUuid: getNewFeatureUuid('product-search-by-image'),
            featureType: 'product-search-by-image',
            imageDataUrl: options.imageDataUrl,
            loadingStatus: 'loading'
        };
        dispatch.featureState.setMenuFeatureItem(null);
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
        dispatch.uiState.setMode('full');
        await dispatch.featureState.startFeature({
            featureType: 'chat',
            chatType: 'general'
        });
        return upload(dispatch, newMainFeatureItem, {
            ...options,
            shouldOpenSidebar
        });
    },
    uploadImage(
        dispatch: AppDispatch,
        state: RootState,
        options: { imageDataUrl: string }
    ) {
        const { mainFeatureItem } = state.featureState;
        if (mainFeatureItem?.featureType !== 'product-search-by-image') {
            return;
        }

        const newMainFeatureItem: ProductSearchByImageFeatureItem = {
            ...mainFeatureItem,
            imageDataUrl: options.imageDataUrl,
            loadingStatus: 'loading'
        };
        dispatch.featureState.setMainFeatureItem(newMainFeatureItem);

        return upload(dispatch, newMainFeatureItem, {
            ...options,
            shouldOpenSidebar: false
        });
    },
    setUploadImageError(
        dispatch: AppDispatch,
        state: RootState,
        options: { message: string }
    ) {
        const { mainFeatureItem } = state.featureState;
        if (mainFeatureItem?.featureType !== 'product-search-by-image') {
            return;
        }

        const newMainFeatureItem: ProductSearchByImageFeatureItem = {
            ...mainFeatureItem,
            message: options.message,
            loadingStatus: 'error'
        };
        return dispatch.featureState.setMainFeatureItem(newMainFeatureItem);
    },
    isFilterable: false
} as const satisfies FeatureHandler<ProductSearchByImageFeatureItem>;
